import {
  AlignCenter,
  AlignLeft,
  AlignRight,
  AppWindow,
  Archive,
  ArrowLeft,
  ArrowRight,
  Atom,
  Award,
  Backpack,
  Bold,
  BookMarked,
  BookOpen,
  Calculator,
  FlaskConical,
  CalendarClock,
  ChartColumn,
  Check,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  ChevronUp,
  CircleAlert,
  CircleCheck,
  CircleQuestionMark,
  Code,
  Copy,
  Download,
  Eye,
  EyeOff,
  FileText,
  FilterX,
  Gem,
  GraduationCap,
  GripVertical,
  Heading1,
  Heading2,
  Heading3,
  Home,
  Image,
  Inbox,
  Info,
  Italic,
  KeyRound,
  Languages,
  Laptop,
  LayoutDashboard,
  Library,
  Lightbulb,
  Microscope,
  Link,
  List,
  ListOrdered,
  LoaderCircle,
  Lock,
  LogIn,
  LogOut,
  Mail,
  Menu,
  Minus,
  Moon,
  Notebook,
  Pencil,
  Presentation,
  OctagonPause,
  Play,
  Plus,
  Quote,
  Redo2,
  RefreshCw,
  Rss,
  RotateCcw,
  Route,
  Save,
  School,
  University,
  Search,
  Send,
  Settings,
  Shield,
  ShieldAlert,
  Sparkles,
  Sun,
  Trash2,
  TriangleAlert,
  Trophy,
  Underline,
  Undo2,
  Upload,
  User,
  UserPlus,
  Users,
  Video,
  WifiOff,
  X,
} from '@lucide/vue'

/**
 * Named Lucide components used in the product. Importing through this map
 * keeps screens on a shared visual language and lets unused icons tree-shake.
 */
export const ICONS = {
  dashboard: LayoutDashboard,
  book: BookOpen,
  library: Library,
  school: School,
  university: University,
  backpack: Backpack,
  microscope: Microscope,
  notebook: Notebook,
  presentation: Presentation,
  'book-marked': BookMarked,
  atom: Atom,
  flask: FlaskConical,
  laptop: Laptop,
  calculator: Calculator,
  lightbulb: Lightbulb,
  route: Route,
  award: Award,
  users: Users,
  chart: ChartColumn,
  settings: Settings,
  shield: Shield,
  user: User,
  menu: Menu,
  moon: Moon,
  sun: Sun,
  x: X,
  'graduation-cap': GraduationCap,
  'chevron-down': ChevronDown,
  'chevron-left': ChevronLeft,
  'chevron-right': ChevronRight,
  'chevron-up': ChevronUp,
  'log-out': LogOut,
  'circle-alert': CircleAlert,
  'circle-check': CircleCheck,
  'octagon-pause': OctagonPause,
  'triangle-alert': TriangleAlert,
  info: Info,
  search: Search,
  inbox: Inbox,
  lock: Lock,
  play: Play,
  'file-text': FileText,
  help: CircleQuestionMark,
  gem: Gem,
  grip: GripVertical,
  rss: Rss,
  download: Download,
  copy: Copy,
  plus: Plus,
  pencil: Pencil,
  trash: Trash2,
  upload: Upload,
  link: Link,
  image: Image,
  youtube: Video,
  window: AppWindow,
  bold: Bold,
  italic: Italic,
  underline: Underline,
  list: List,
  'list-ordered': ListOrdered,
  quote: Quote,
  code: Code,
  'align-left': AlignLeft,
  'align-center': AlignCenter,
  'align-right': AlignRight,
  h1: Heading1,
  h2: Heading2,
  h3: Heading3,
  minus: Minus,
  eye: Eye,
  'eye-off': EyeOff,
  'log-in': LogIn,
  'user-plus': UserPlus,
  home: Home,
  'shield-alert': ShieldAlert,
  refresh: RefreshCw,
  check: Check,
  'wifi-off': WifiOff,
  languages: Languages,
  mail: Mail,
  key: KeyRound,
  'arrow-right': ArrowRight,
  'arrow-left': ArrowLeft,
  'filter-x': FilterX,
  sparkles: Sparkles,
  trophy: Trophy,
  calendar: CalendarClock,
  archive: Archive,
  undo: Undo2,
  redo: Redo2,
  restore: RotateCcw,
  save: Save,
  send: Send,
  loader: LoaderCircle,
} as const

export type IconName = keyof typeof ICONS

/** Lucide icons tagged around education. Stable pick per journey id. */
const JOURNEY_COVER_ICONS = [
  'graduation-cap',
  'book',
  'library',
  'school',
  'university',
  'backpack',
  'microscope',
  'notebook',
  'presentation',
  'book-marked',
  'atom',
  'flask',
  'laptop',
  'calculator',
  'lightbulb',
  'languages',
] as const satisfies readonly IconName[]

export function journeyCoverIcon(id: string): IconName {
  let hash = 0
  for (let i = 0; i < id.length; i += 1) hash = (hash * 31 + id.charCodeAt(i)) >>> 0
  return JOURNEY_COVER_ICONS[hash % JOURNEY_COVER_ICONS.length]!
}

export function lessonIcon(type: string, state?: 'completed' | 'locked'): IconName {
  if (state === 'completed') return 'check'
  if (state === 'locked') return 'lock'
  if (type === 'video') return 'play'
  if (type === 'quiz') return 'help'
  return 'file-text'
}
